import type { ExistingEGiftCardCampaignResponse } from '../types/eGiftCard.types';

export interface DisabledDateInfo {
    date: Date;
    tooltipText: string;
}

/**
 * Generates disabled dates from existing e-gift-card campaigns.
 * @param brandId - The selected brand ID
 * @param existingEGiftCardCampaigns - Existing campaign responses
 * @returns Array of disabled date objects with tooltip information
 */
export function getDisabledDatesForBrand(
    brandId: string,
    existingEGiftCardCampaigns: ExistingEGiftCardCampaignResponse[],
    excludedCouponId?: string,
): DisabledDateInfo[] {
    if (!brandId || existingEGiftCardCampaigns.length === 0) {
        return [];
    }

    const disabledDates: DisabledDateInfo[] = [];

    existingEGiftCardCampaigns
      .filter((campaign) => campaign.coupon_id !== excludedCouponId)
      .forEach((campaign) => {
        const startDate = new Date(campaign.valid_from);
        const endDate = new Date(campaign.valid_to);

        // Format the date range for tooltip
        const startFormatted = startDate.toLocaleDateString('en-US', {
            month: 'short',
            day: '2-digit',
        });
        const endFormatted = String(endDate.getDate()).padStart(2, '0');
        const tooltipText = `These dates belong to Campaign (${startFormatted}-${endFormatted})`;

        // Generate all dates in the range (inclusive)
        const currentDate = new Date(startDate);
        while (currentDate <= endDate) {
            disabledDates.push({
                date: new Date(currentDate),
                tooltipText: tooltipText,
            });
            currentDate.setDate(currentDate.getDate() + 1);
        }
      });

    return disabledDates;
}

export function hasOverlappingCampaign(
    validFrom: Date,
    validTo: Date,
    existingEGiftCardCampaigns: ExistingEGiftCardCampaignResponse[],
    excludedCouponId?: string,
): boolean {
    return existingEGiftCardCampaigns
        .filter((campaign) => campaign.coupon_id !== excludedCouponId)
        .some((campaign) => {
            const campaignStart = new Date(campaign.valid_from);
            const campaignEnd = new Date(campaign.valid_to);
            return validFrom <= campaignEnd && validTo >= campaignStart;
        });
}
